"use client"

import { useState, useMemo, useCallback } from "react"
import { ThemeDivider } from "../terra/component/ThemeDivider/ThemeDivider"
import {
  LayoutFilterableCards,
  type FilterGroup,
  type LayoutFilterableCardItem,
} from "../terra/layouts/LayoutFilterableCards/LayoutFilterableCards"
import { documentToHtmlString } from "@contentful/rich-text-html-renderer"
import { handleButtons, safeSlug } from "@/lib/utils"
import type { FilterableCardsLabelsFields } from "@/queries/fragments/filterableCardsLabelsFragment"

const DEFAULT_PAGE_SIZE = 30

/** Mirrors the `range: { min: 3 }` validation on layoutFilterableCards.minPageSize. */
const MIN_PAGE_SIZE = 3

/**
 * Filter group id used for the synthetic "Product Capabilities" group that is
 * derived from the cards themselves (only when displayType === "Filterable Card").
 * Kept distinct from Contentful filter-category slugs so applyFilters can match
 * selected values against `_capabilitySlugs` instead of `_tagSlugs`.
 */
const CAPABILITIES_GROUP_ID = "product-capabilities"

/**
 * Filter group id for the synthetic "Offers" group derived from each card's
 * `offers` MXP Tag references (only when displayType === "Filterable Card").
 * Renders at the very top of the filter sidebar.
 */
const OFFERS_GROUP_ID = "offers"

function slugifyValue(value: string): string {
  return (value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

function productTypeEyebrow(
  productType: string | undefined,
  labels?: FilterableCardsLabelsFields | null,
): string | undefined {
  switch (productType) {
    case "Hardware":
      return labels?.labelForProductTypeHardware || "Hardware"
    case "Software":
      return labels?.labelForProductTypeSoftware || "Software"
    case "Services":
      return labels?.labelForProductTypeServices || "Services"
    default:
      return productType
  }
}

/**
 * Resolves the authored `minPageSize` into a usable page size. Falls back to
 * DEFAULT_PAGE_SIZE when the field is empty (every entry created before the
 * field existed) and floors at MIN_PAGE_SIZE so a bad value can't produce an
 * unusable grid.
 */
// function resolveMinPageSize(minPageSize: unknown): number {
//   const parsed = Math.floor(Number(minPageSize))
//   if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_PAGE_SIZE
//   return Math.max(parsed, MIN_PAGE_SIZE)
// }

function defaultSortToValue(defaultSort: string): string {
  const map: Record<string, string> = {
    Featured: "featured",
    "A-Z": "a-z",
    "Z-A": "z-a",
  }
  return map[defaultSort] || "a-z"
}

function buildFilterGroups(categories: any[]): FilterGroup[] {
  return categories
    .filter(Boolean)
    .map((cat: any) => ({
      id: cat.slug,
      label: cat.displayName,
      isCollapsedByDefault: Boolean(cat.isCollapsedByDefault),
      options: (cat.tagsCollection?.items || [])
        .filter((tag: any) => tag && !tag.isHidden)
        .map((tag: any) => ({
          label: tag.displayName,
          value: tag.slug,
        })),
    }))
    .filter(g => g.options.length > 0)
}

/**
 * Builds a synthetic "Capabilities" filter group from the union of all
 * capabilities present on the normalized cards. Each option's `value` is the
 * capability slug (matching the card's hidden `_capabilitySlugs`) and its
 * `label` is the capability title. Returns null when no card has capabilities.
 *
 * Collapsed by default: this group is always rendered at the bottom of the
 * sidebar so it starts collapsed to reduce visual clutter.
 */
function buildCapabilityFilterGroup(
  rawCards: any[],
  label: string,
): FilterGroup | null {
  const seen = new Map<string, string>()

  rawCards.filter(Boolean).forEach((card: any) => {
    const caps: any[] = card.productCapabilitiesCollection?.items || []
    caps.filter(Boolean).forEach((cap: any) => {
      const title = cap.title || cap.internalTitle || ""
      const slug = slugifyValue(title)
      if (slug && title && !seen.has(slug)) {
        seen.set(slug, title)
      }
    })
  })

  if (seen.size === 0) return null

  return {
    id: CAPABILITIES_GROUP_ID,
    label,
    isCollapsedByDefault: true,
    options: Array.from(seen, ([value, label]) => ({ label, value })).sort(
      (a, b) => a.label.localeCompare(b.label),
    ),
  }
}

/**
 * Builds a synthetic "Offers" filter group from the union of all MXP Tag
 * references authored on each card's `offers` field. Each option's `value` is
 * the tag slug (matching the card's hidden `_offerSlugs`) and its `label` is
 * the tag's display name. Returns null when no card has any offers attached.
 *
 * This group is intentionally always expanded on load (isCollapsedByDefault
 * defaults to `undefined`/false) and is rendered at the very top of the
 * filter sidebar by the caller.
 */
function buildOffersFilterGroup(
  rawCards: any[],
  label: string,
): FilterGroup | null {
  const seen = new Map<string, string>()

  rawCards.filter(Boolean).forEach((card: any) => {
    const offers: any[] = card.offersCollection?.items || []
    offers.filter(Boolean).forEach((offer: any) => {
      const label = offer.displayName || ""
      const slug = offer.slug || ""
      if (slug && label && !seen.has(slug)) {
        seen.set(slug, label)
      }
    })
  })

  if (seen.size === 0) return null

  return {
    id: OFFERS_GROUP_ID,
    label,
    options: Array.from(seen, ([value, label]) => ({ label, value })).sort(
      (a, b) => a.label.localeCompare(b.label),
    ),
  }
}

function normalizePageToCard(
  page: any,
  lang: string,
  labels?: FilterableCardsLabelsFields | null,
): LayoutFilterableCardItem & { _tagSlugs: string[]; _title: string } {
  const slug = `/${lang}/${safeSlug(page.slug)}`
  const seo = page.pageSeoCollection?.items?.[0]
  const developer = page.developerName || undefined
  // const developerUrl = page.developerUrl || undefined
  const teaserImage = seo?.teaserImage?.image
  const tags: any[] = page.tagsCollection?.items || []
  const title = seo?.teaserTitle || page.publicTitle || ""
  const categoryTags = tags
    .filter((t: any) => t && !t.isHidden)
    .map((t: any) => t.displayName)
    .filter((name: unknown): name is string => typeof name === "string" && name.length > 0)

  return {
    // cardVariant: "filterable" as const,
    header: title,
    developer: developer,
    // description: seo?.teaserDescription || "",
    button: {
      label: labels?.labelForLearnMore || "Learn More",
      destination: slug,
    },
    image: teaserImage?.url
      ? {
          url: teaserImage.url,
          alt: seo?.teaserImage?.altText || title,
          width: teaserImage.width,
          height: teaserImage.height,
        }
      : undefined,
    imageDisplayMode: teaserImage?.url ? "teaser" : "logo",
    categoryTags,
    _tagSlugs: tags.map((t: any) => t.slug),
    _title: title.toLowerCase(),
  }
}

/**
 * Maps a raw `filterableCard` Contentful entry (as returned by
 * `filterableCardsQuery.ts` when `displayType === "Filterable Card"`) onto
 * the props expected by `<FilterableCard>`.
 *
 * Field mapping:
 *   filterableCard.title              → header
 *   filterableCard.subtitle           → subHeader
 *   filterableCard.image              → image (Image entry: asset + altText)
 *   filterableCard.cta                → button          (via handleButtons)
 *   filterableCard.secondaryCta       → secondaryButton (via handleButtons)
 *   filterableCard.featuredCard       → featured
 *   filterableCard.productCapabilities → productCapabilities[]
 *   filterableCard.offers             → offers[] (display-name chips)
 *
 * The hidden `_tagSlugs` / `_offerSlugs` / `_title` fields keep this row
 * compatible with applyFilters/applySort, just like normalizePageToCard.
 */
function normalizeFilterableCardToCard(
  card: any,
  labels?: FilterableCardsLabelsFields | null,
): LayoutFilterableCardItem & {
  _tagSlugs: string[]
  _title: string
  _capabilitySlugs: string[]
  _offerSlugs: string[]
  _featured: boolean
} {
  const tags: any[] = card.tagsCollection?.items || []
  const offers: any[] = (card.offersCollection?.items || []).filter(Boolean)
  const title = card.title || card.internalTitle || ""

  const offerChips = offers
    .map((offer: any) => ({
      label: (offer?.displayName || "").trim(),
      slug: typeof offer?.slug === "string" ? offer.slug : "",
    }))
    .filter((o) => o.label.length > 0 && o.slug.length > 0)

  const primaryButton = card.cta
    ? handleButtons([card.cta])?.[0]
    : undefined
  const secondaryButton = card.secondaryCta
    ? handleButtons([card.secondaryCta])?.[0]
    : undefined

  const capabilityItems: any[] = card.productCapabilitiesCollection?.items || []
  const productCapabilities = capabilityItems
    .filter(Boolean)
    .map((cap: any) => ({
      title: cap.title || cap.internalTitle || "",
      icon: cap.icon || undefined,
      description: cap.description || undefined,
    }))
    .filter((cap: any) => cap.title)

  const imageAsset = card.image?.image
  const image = imageAsset?.url
    ? {
        url: imageAsset.url,
        alt: card.image?.altText || card.image?.title || title,
        width: imageAsset.width,
        height: imageAsset.height,
      }
    : undefined

  return {
    eyebrow: productTypeEyebrow(card?.productType?.[0], labels),
    header: title,
    subHeader: card.subtitle || undefined,
    button: primaryButton
      ? { label: primaryButton.label, destination: primaryButton.destination }
      : undefined,
    secondaryButton: secondaryButton
      ? { label: secondaryButton.label, destination: secondaryButton.destination }
      : undefined,
    featured: Boolean(card.featuredCard),
    productCapabilities:
      productCapabilities.length > 0 ? productCapabilities : undefined,
    offers: offerChips.length > 0
      ? offerChips.map((o) => ({ label: o.label }))
      : undefined,
    image,
    _tagSlugs: tags
      .map((t: any) => t?.slug)
      .filter((s: unknown): s is string => typeof s === "string"),
    _capabilitySlugs: productCapabilities
      .map((cap: any) => slugifyValue(cap.title))
      .filter((s: string) => s.length > 0),
    _offerSlugs: offerChips.map((o) => o.slug),
    _featured: Boolean(card.featuredCard),
    _title: title.toLowerCase(),
  }
}

type NormalizedCard = LayoutFilterableCardItem & {
  _tagSlugs: string[]
  _title: string
  _capabilitySlugs?: string[]
  _offerSlugs?: string[]
  _featured?: boolean
}


function applyFilters(
  cards: NormalizedCard[],
  activeFilters: Record<string, string[]>,
): NormalizedCard[] {
  const activeGroups = Object.entries(activeFilters).filter(
    ([, vals]) => vals.length > 0,
  )
  if (activeGroups.length === 0) return cards

  return cards.filter(card =>
    activeGroups.every(([groupId, values]) => {
      let haystack: string[]
      if (groupId === CAPABILITIES_GROUP_ID) {
        haystack = card._capabilitySlugs || []
      } else if (groupId === OFFERS_GROUP_ID) {
        haystack = card._offerSlugs || []
      } else {
        haystack = card._tagSlugs
      }
      return values.some(v => haystack.includes(v))
    }),
  )
}

function applySort(cards: NormalizedCard[], sort: string) {
  const sorted = [...cards]
  switch (sort) {
    case "featured":
      // Featured cards float to the top; ties break alphabetically.
      sorted.sort((a, b) => {
        const af = a._featured ? 1 : 0
        const bf = b._featured ? 1 : 0
        if (af !== bf) return bf - af
        return a._title.localeCompare(b._title)
      })
      break
    case "z-a":
      sorted.sort((a, b) => b._title.localeCompare(a._title))
      break
    case "a-z":
      sorted.sort((a, b) => a._title.localeCompare(b._title))
      break
    default:
      sorted.sort((a, b) => a._title.localeCompare(b._title))
  }
  return sorted
}

export default function MXPLayoutFilterableCards(props: any) {
  const { value, index, divider, lang, headerElement, isLCP, config } = props

  const labels = config?.filterableCardsLabels

  const sortOptions = useMemo(() => {
    return [
      { label: labels?.labelForFeatured || "Featured", value: "featured" },
      { label: labels?.labelForSortAz || "A - Z", value: "a-z" },
      { label: labels?.labelForSortZa || "Z - A", value: "z-a" },
    ]
  }, [labels])

  const initialSort = defaultSortToValue(value.defaultSort || "A-Z")
  const minPageSize = value.minPageSize || DEFAULT_PAGE_SIZE
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>(
    {},
  )
  const [currentSort, setCurrentSort] = useState(initialSort)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(minPageSize)

  // "Results" dropdown offers the authored minimum and its multiples.
  const pageSizeOptions = useMemo(
    () => [minPageSize, minPageSize * 2, minPageSize * 3],
    [minPageSize],
  )

  const buttons: any[] = handleButtons(value.buttonsCollection?.items, true, lang)

  const categoryFilterGroups = useMemo(
    () => buildFilterGroups(value.filterCategoriesCollection?.items || []),
    [value.filterCategoriesCollection],
  )

  const isFilterableVariant = value.displayType === "Filterable Card"

  const allCards = useMemo(() => {
    const rawItems: any[] = (value.filterableCards || []).filter(Boolean)

    if (isFilterableVariant) {
      return rawItems
        .filter((card: any) => card.showOnFrontend !== false)
        .map((card: any) => normalizeFilterableCardToCard(card, labels))
    }

    return rawItems.map((page: any) =>
      normalizePageToCard(page, lang || "en", labels),
    )
  }, [value.filterableCards, isFilterableVariant, lang, labels])

  /**
   * Only the "Filterable Card" variant exposes product capabilities and
   * offers, so those synthetic groups are attached alongside the category
   * filter groups in that case. The "Offers" group is intentionally
   * rendered at the very top of the sidebar, ahead of any author-defined
   * tag categories.
   */
  const filterGroups = useMemo(() => {
    if (!isFilterableVariant) return categoryFilterGroups

    const rawItems: any[] = (value.filterableCards || []).filter(Boolean)
    const capabilityGroup = buildCapabilityFilterGroup(
      rawItems,
      labels?.labelForCapabilities || "Capabilities",
    )
    const offersGroup = buildOffersFilterGroup(
      rawItems,
      labels?.labelForOffers || "Offers",
    )

    return [
      ...(offersGroup ? [offersGroup] : []),
      ...categoryFilterGroups,
      ...(capabilityGroup ? [capabilityGroup] : []),
    ]
  }, [categoryFilterGroups, isFilterableVariant, value.filterableCards, labels])

  const filteredCards = useMemo(
    () => applySort(applyFilters(allCards, activeFilters), currentSort),
    [allCards, activeFilters, currentSort],
  )

  const totalResults = filteredCards.length
  const totalPages = Math.max(1, Math.ceil(totalResults / pageSize))
  const safePage = Math.min(currentPage, totalPages)
  const pageCards = filteredCards.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  )

  const handleFilterChange = useCallback(
    (groupId: string, values: string[]) => {
      setActiveFilters(prev => ({ ...prev, [groupId]: values }))
      setCurrentPage(1)
    },
    [],
  )

  const handleClearAll = useCallback(() => {
    setActiveFilters({})
    setCurrentPage(1)
  }, [])

  const handleSortChange = useCallback((val: string) => {
    setCurrentSort(val)
    setCurrentPage(1)
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size)
    setCurrentPage(1)
  }, [])

  const dividerClasses = `section-divider-wrap ${value.theme}`

  return (
    <>
      <LayoutFilterableCards
        key={index}
        isLCP={isLCP}
        headerElement={headerElement}
        eyebrow={value.eyebrow}
        header={value.header}
        subheader={value.subheader}
        description={documentToHtmlString(value?.description?.json)}
        presetTheme={value.theme}
        isHeaderBig={value.isHeaderBig || false}
        isWrapped={true}
        buttons={buttons}
        cards={pageCards}
        totalResults={totalResults}
        filterGroups={filterGroups}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
        onClearAllFilters={handleClearAll}
        sortOptions={sortOptions}
        currentSort={currentSort}
        onSortChange={handleSortChange}
        currentPage={safePage}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        displayType={value.displayType}
        labels={labels}
      />
      {divider && (
        <div className={dividerClasses}>
          <ThemeDivider currentTheme={value.theme} />
        </div>
      )}
    </>
  )
}
